import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, SxProps, Theme, Typography, useTheme } from '@mui/material';
import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import React, { useCallback } from 'react';
import Highlighter from 'react-highlight-words';
import { useDispatch, useSelector } from 'react-redux';

import { FALLBACK_CURRENCY_CODE } from '../../../constants/shell';
import { COLUMN_IDS } from '../../../constants/users';
import { useCurrency } from '../../../contexts/CurrencyProvider';
import { variables } from '../../../contexts/ThemeProvider';
import useColumnsVisibility from '../../../hooks/useColumnsVisibility';
import usePopover from '../../../hooks/usePopover';
import { changeUserColumnsVisibility } from '../../../store/actions/users';
import { selectUsersVisibleColumns } from '../../../store/selectors/users';
import { IUser } from '../../../types/users';
import { getFullName } from '../../../utils/helpers';
import { getUserBudgetStatusClassname, getUserRoleLabel } from '../../../utils/users';
import { Avatar, Price } from '../../index';
import { UsersContextMenu } from '../../_mui/_components_';
import { CopyableButton } from '../../_mui/_controls_';
import { TableMUI } from '../../_mui/_tables_';
import ColumnsVisibilityPopover from '../ColumnsVisibilityPopover/ColumnsVisibilityPopover';
import { useUsersContextMenu } from '../hooks/useUsersContextMenu';

import styles from './ListView.module.scss';

interface IProps {
  users: IUser[];
  searchQuery: string;
  handleRowClick: (user: IUser) => void;
  getDepartmentLabel: (id: string) => string | undefined;
  renderIntegrations: (user: Partial<IUser>) => React.ReactNode;
  onDelete: (id: string) => void;
  onResetPassword: (email: string) => void;
  checkCanEditUser: (user: IUser) => boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
}

const ListView = ({
  users,
  handleRowClick,
  searchQuery,
  getDepartmentLabel,
  renderIntegrations,
  onDelete,
  onResetPassword,
  checkCanEditUser,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}: IProps) => {
  const initialVisibleColumns = useSelector(selectUsersVisibleColumns);
  const dispatch = useDispatch();
  const theme = useTheme();
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number } | null>(null);

  const {
    visibleColumns,
    columnSearchQuery,
    setColumnSearchQuery,
    handleColumnVisibilityChange,
    handleToggleAllColumns,
    handleResetColumns,
    areAllColumnsVisible,
  } = useColumnsVisibility({
    initialState: initialVisibleColumns,
    requiredColumnIds: [COLUMN_IDS.USER, COLUMN_IDS.ACTIONS],
    onChange: (newState: Record<string, boolean>) => dispatch(changeUserColumnsVisibility(newState)),
  });

  const {
    menuAnchorEl,
    handleMenuOpen,
    handleMenuClose,
    handleEdit,
    handleDelete,
    handleResetPassword,
    handleSkipCrm,
    isSkipCrmAllowedInOrg,
    selectedItem,
  } = useUsersContextMenu<IUser>({
    onEdit: handleRowClick,
    onDelete,
    onResetPassword,
    canEdit: checkCanEditUser,
  });

  const {
    anchorEl: visibleColsAnchorEl,
    position: popoverPosition,
    isOpen: isVisibleColsPopoverOpen,
    handleOpen: handleVisibleColsMenuOpen,
    handleClose: handleVisibleColsMenuClose,
  } = usePopover();

  const { getRequiredPriceAndCurrency, displayCurrency } = useCurrency();

  const defaultColumnEvents = React.useMemo(
    () => ({
      onBodyCellDoubleClick: handleRowClick,
      onCellContextMenu: (e: React.MouseEvent<HTMLElement, MouseEvent>, user: IUser, columnId?: string) => {
        if (columnId !== COLUMN_IDS.ACTIONS) {
          e.preventDefault();
          setMenuPosition({
            top: e.pageY,
            left: e.pageX,
          });
          handleMenuOpen(e, user);
        }
      },
    }),
    [handleMenuOpen, handleRowClick],
  );

  const handleLocalMenuClose = useCallback(() => {
    setMenuPosition(null);
    handleMenuClose();
  }, [handleMenuClose]);

  const allColumns = React.useMemo(() => {
    const getStickyColumnStyle = (params: {
      theme: Theme;
      position: 'left' | 'right';
      border?: string;
      extraStyles?: SxProps<Theme>;
    }): SxProps<Theme> => {
      const { position, border, extraStyles = {} } = params;

      return {
        ...(position === 'left'
          ? {
              position: { xs: 'static', sm: 'sticky' },
              left: { xs: 'auto', sm: 0 },
              borderRight: border || '1px solid #0000001F',
            }
          : {
              position: 'sticky',
              right: 0,
              borderLeft: border || `1px solid ${variables.tableBorderColor}`,
              boxShadow: variables.tableShadow,
            }),
        backgroundColor: theme.sass.white,
        zIndex: position === 'left' ? 9 : 8,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme.sass.white,
          zIndex: -1,
        },
        [`tr[data-has-menu="true"] &::before`]: {
          backgroundColor: theme.sass.muiDefaultTableHoverColor,
        },
        '.MuiTableRow-hover:hover &::before': {
          backgroundColor: theme.sass.muiDefaultTableHoverColor,
        },
        ...extraStyles,
      };
    };

    const bodyCellStyle = {
      cursor: 'pointer',
    };

    const stickyLeftCellStyle: SxProps<Theme> = getStickyColumnStyle({
      theme,
      position: 'left',
      extraStyles: {
        cursor: 'pointer',
      },
    });

    const actionsCellStyle: SxProps<Theme> = getStickyColumnStyle({
      theme,
      position: 'right',
      extraStyles: {
        textAlign: 'center',
        width: 60,
        '& .MuiSvgIcon-root': {
          position: 'relative',
          top: '2px',
        },
      },
    });

    return [
      {
        id: COLUMN_IDS.USER,
        label: 'User',
        ...defaultColumnEvents,
        headerCellStyle: {
          ...stickyLeftCellStyle,
          zIndex: `11 !important`,
          paddingLeft: 2.5,
        },
        bodyCellStyle: {
          ...stickyLeftCellStyle,
          cursor: 'pointer',
          width: 300,
          flex: '0 0 auto',
        },
        render: (user: IUser) => {
          const { first_name, last_name, picture, email } = user;
          const fullName = getFullName(first_name, last_name);

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Avatar image={picture} />
              <Box sx={{ minWidth: 0 }}>
                <CopyableButton
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    lineHeight: '1.2em',
                    maxHeight: '2.4em',
                    textAlign: 'start',
                    color: theme.sass.ebony,
                    fontWeight: theme.typography.fontWeightMedium,
                  }}
                  value={fullName}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                  }}
                >
                  <Highlighter
                    highlightClassName={styles.mark}
                    searchWords={[searchQuery]}
                    autoEscape
                    textToHighlight={fullName}
                  />
                </CopyableButton>
                <CopyableButton
                  style={{
                    width: '100%',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    textAlign: 'start',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: theme.sass.ebony,
                  }}
                  value={email}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                  }}
                >
                  <Highlighter
                    highlightClassName={styles.mark}
                    className={styles.text}
                    searchWords={[searchQuery]}
                    autoEscape
                    textToHighlight={email}
                  />
                </CopyableButton>
              </Box>
            </Box>
          );
        },
      },
      {
        id: COLUMN_IDS.INTEGRATIONS,
        label: 'Integrations',
        ...defaultColumnEvents,
        bodyCellStyle,
        render: (user: IUser) => renderIntegrations({ integration: user.integration, uid: user.uid }),
      },
      {
        id: COLUMN_IDS.ROLE,
        label: 'Role',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => <Typography>{getUserRoleLabel(user.admin_type)}</Typography>,
      },
      {
        id: COLUMN_IDS.DEPARTMENT,
        label: 'Department',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => <Typography>{getDepartmentLabel(user.department_id)}</Typography>,
      },
      {
        id: COLUMN_IDS.BUDGET_LEFT,
        label: 'Budget left',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => {
          const remainingBudget =
            user.available_budget - user.used_budget < 0 ? 0 : Math.floor(user.available_budget - user.used_budget);
          return (
            <Price
              value={
                displayCurrency
                  ? getRequiredPriceAndCurrency({ price: remainingBudget, currency_code: user.budget_currency })
                  : { price: remainingBudget, currency: user.budget_currency || FALLBACK_CURRENCY_CODE }
              }
              thousandSeparator
              fixedDecimalScale={false}
              className={cn(
                styles.budgetLeft,
                styles[getUserBudgetStatusClassname(user.available_budget, user.used_budget)],
              )}
            />
          );
        },
      },
      {
        id: COLUMN_IDS.BUDGET_SPENT,
        label: 'Budget spent',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => (
          <Price
            value={
              displayCurrency
                ? getRequiredPriceAndCurrency({
                    price: Math.floor(user.used_budget),
                    currency_code: user.budget_currency,
                  })
                : { price: user.used_budget, currency: user.budget_currency || FALLBACK_CURRENCY_CODE }
            }
            thousandSeparator
            fixedDecimalScale={false}
          />
        ),
      },
      {
        id: COLUMN_IDS.BUDGET_TOTAL,
        label: 'Budget total',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => (
          <Price
            value={
              displayCurrency
                ? getRequiredPriceAndCurrency({
                    price: Math.floor(user.available_budget),
                    currency_code: user.budget_currency,
                  })
                : { price: user.available_budget, currency: user.budget_currency || FALLBACK_CURRENCY_CODE }
            }
            thousandSeparator
            fixedDecimalScale={false}
            className={styles.budgetAvailable}
          />
        ),
      },
      {
        id: COLUMN_IDS.LAST_LOGIN,
        label: 'Last login',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => {
          const lastLoginDate = user.last_sign_in_time ? format(parseISO(user.last_sign_in_time), 'dd/MM/yyyy') : '-';
          return <Typography>{lastLoginDate}</Typography>;
        },
      },
      {
        id: COLUMN_IDS.ACTIONS,
        label: <TuneIcon />,
        headerCellStyle: actionsCellStyle,
        bodyCellStyle: actionsCellStyle,
        onHeaderCellClick: handleVisibleColsMenuOpen,
        render: (user: IUser) => (
          <Box
            role="button"
            sx={{
              cursor: 'pointer',
            }}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              setMenuPosition({
                top: rect.bottom,
                left: rect.left - 140,
              });
              handleMenuOpen(e, user);
            }}
          >
            <MoreHorizIcon />
          </Box>
        ),
      },
    ];
  }, [
    theme,
    defaultColumnEvents,
    handleVisibleColsMenuOpen,
    searchQuery,
    renderIntegrations,
    getDepartmentLabel,
    displayCurrency,
    getRequiredPriceAndCurrency,
    handleMenuOpen,
  ]);

  const columns = React.useMemo(() => {
    return allColumns.filter((column) => visibleColumns[column.id]);
  }, [allColumns, visibleColumns]);

  return (
    <div className={styles.list}>
      <TableMUI
        activeRow={selectedItem}
        handleMenuClose={handleLocalMenuClose}
        maxHeight="calc(100vh - 315px)"
        containerStyle={{ width: '100%' }}
        data={users}
        columns={columns}
        page={page}
        rowsPerPage={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        isLoading={false}
        emptyPlaceholderTitle="No users found"
        emptyPlaceholderSubtitle=""
      />
      <UsersContextMenu
        anchorEl={menuAnchorEl}
        menuPosition={menuPosition}
        selectedItem={selectedItem}
        isSkipCrmAllowedInOrg={isSkipCrmAllowedInOrg}
        checkCanEditUser={checkCanEditUser}
        onClose={handleLocalMenuClose}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onResetPassword={handleResetPassword}
        onSkipCrm={handleSkipCrm}
      />
      <ColumnsVisibilityPopover
        anchorEl={visibleColsAnchorEl}
        position={popoverPosition}
        open={isVisibleColsPopoverOpen}
        onClose={handleVisibleColsMenuClose}
        columns={allColumns}
        visibleColumns={visibleColumns}
        searchQuery={columnSearchQuery}
        onSearchChange={setColumnSearchQuery}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        onToggleAll={handleToggleAllColumns}
        onReset={handleResetColumns}
        requiredColumnIds={[COLUMN_IDS.USER, COLUMN_IDS.ACTIONS]}
        areAllColumnsVisible={areAllColumnsVisible}
      />
    </div>
  );
};

export default ListView;
